import React from "react";
import { Truck, Zap, ShieldCheck, Leaf } from "lucide-react";

import rice from "../../assets/rice.webp";
import sugar from "../../assets/sugar.webp";
import redChili from "../../assets/chilies.webp";
import chickpeas from "../../assets/checkpeas.webp";
import lentils from "../../assets/lentis.webp";
import flour from "../../assets/flour.webp";
import coriander from "../../assets/coriander.png";
import corn from "../../assets/corn.webp";
import onions from "../../assets/onion.png";
import potatoes from "../../assets/potatos.png";
import garlic from "../../assets/garlic.webp";
import spices from "../../assets/spices.webp";

const SpicesServices = ({ company }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 text-slate-900">
    <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

      {/* HEADER */}
      <div className="text-center mb-16 lg:mb-20">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-6 bg-gradient-to-r from-slate-800 via-green-900 to-emerald-800 bg-clip-text text-transparent drop-shadow-2xl">
          What We Do
        </h2>
        <div className="w-24 h-1.5 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto rounded-full shadow-xl" />
        <p className="text-lg sm:text-xl lg:text-2xl text-slate-700 mt-6 max-w-3xl mx-auto bg-white/80 backdrop-blur-sm p-6 rounded-3xl shadow-xl">
          Premium organic spice solutions from farm to global markets
        </p>
      </div>

      {/* SERVICES (REDUCED SIZE) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
        {company.services.map((service, i) => (
          <div
            key={i}
            className="group relative p-6 sm:p-7 lg:p-8 bg-white/80 backdrop-blur-xl rounded-3xl border border-green-100 hover:border-green-400 hover:shadow-xl hover:-translate-y-2 transition-all duration-500"
          >
            {/* ICON */}
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 text-white rounded-2xl flex items-center justify-center shadow-xl">
              {i === 0 && <Truck size={20} />}
              {i === 1 && <Leaf size={20} />}
              {i === 2 && <ShieldCheck size={20} />}
            </div>

            <div className="pt-14 text-center">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-black mb-4 text-slate-900">
                {service.title}
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-slate-700 mb-6">
                {service.desc}
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <Zap size={16} className="text-green-600" />
                  <span className="text-sm font-semibold">Fast global delivery</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <ShieldCheck size={16} className="text-green-600" />
                  <span className="text-sm font-semibold">
                    Government Approved Lab Certified
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <Truck size={16} className="text-green-600" />
                  <span className="text-sm font-semibold">Flexible MOQ available</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PREMIUM COLLECTION */}
      <div className="mt-20">
        <div className="text-center mb-14">
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-slate-800 via-green-900 to-emerald-800 bg-clip-text text-transparent">
            Our Premium Collection
          </h3>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto">
            From global commodities to premium sundries
          </p>
        </div>

        {/* BIGGER IMAGES */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto mb-16">
          {[
            { label: "Rice", image: rice },
            { label: "Sugar", image: sugar },
            { label: "Red Chilies", image: redChili },
            { label: "Chickpeas", image: chickpeas },
            { label: "Red Lentils", image: lentils },
            { label: "Wheat Flour", image: flour },
            { label: "Coriander", image: coriander },
            { label: "Corn Maize", image: corn },
            { label: "Onions", image: onions },
            { label: "Potatoes", image: potatoes },
            { label: "Garlic", image: garlic },
            { label: "Spices", image: spices },
          ].map((item, idx) => (
            <div
              key={idx}
              className="group bg-white/80 backdrop-blur-sm p-4 rounded-3xl border border-green-200 hover:border-green-400 hover:shadow-2xl transition-all"
            >
              <div className="w-full h-36 sm:h-44 lg:h-52 rounded-2xl overflow-hidden mb-3">
                <img
                  src={item.image}
                  alt={item.label}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <p className="text-sm font-semibold text-center text-slate-700">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* FOOTER TEXT */}
        <div className="text-center">
          <p className="text-base text-slate-600 max-w-2xl mx-auto bg-white/60 backdrop-blur-sm p-6 rounded-3xl border border-green-100">
            Premium quality • Sustainably sourced • Export ready
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default SpicesServices;
